import React, { MutableRefObject, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileImport, faFile } from "@fortawesome/pro-light-svg-icons";
import { Trans } from "react-i18next";
import { Button } from "@unleashed/components/ui/Button";
import classNames from "classnames";
import { Heading } from "../../ui/components/Heading";
import { t } from "i18next";
import { displayFileSize } from "../../../utils/displayFileSizeUtil";

type MultipleFileUploadModalProps = {
  showModal: boolean;
  handleShowModal: (show: boolean) => void;
  acceptedFileTypes: string;
  maxFileSizeBytes: number;
  modalHeader: string;
  onSubmit: (files: File[]) => void;
};

const MultipleFileUploadModal = ({
  showModal,
  handleShowModal,
  acceptedFileTypes,
  maxFileSizeBytes,
  modalHeader,
  onSubmit,
}: MultipleFileUploadModalProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const inputRef: MutableRefObject<HTMLInputElement | null> = useRef(null);

  useEffect(() => {
    if (!showModal) {
      setFiles([]);
    }
  }, [showModal]);

  const isInvalidFileSize = (size: number) => {
    return size <= 0 || size >= maxFileSizeBytes;
  };

  const anyInvalidFiles = files.some((file) => isInvalidFileSize(file.size));
  const noFilesSelected = files.length === 0;

  const handleDrag = (event: React.DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "dragenter" || event.type === "dragover") {
      setDragActive(true);
    } else if (event.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    if (event.dataTransfer?.files?.length) {
      const newFiles = Array.from(event.dataTransfer.files);
      setFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      const newFiles = Array.from(event.target.files);
      setFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleSubmit = () => {
    onSubmit(files);
    handleShowModal(false);
  };

  return showModal ? (
    <div className="fixed top-0 left-0 bottom-0 right-0 h-screen w-screen backdrop-blur-sm flex items-center justify-center">
      <form
        className="min-h-max bg-white border-2 border-unleashed-pink rounded -mt-16 max-w-xs w-80"
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="border-b-2 border-unleashed-pink py-2 px-4 w-full">
          <Heading className="text-4xl" size="lg">
            {modalHeader}
          </Heading>
        </div>

        <div className="flex flex-col p-4">
          <div
            className={classNames(
              "min-h-max border-tertiary border-dashed border-2 p-4 rounded opacity-75 w-full",
              "hover:opacity-100 transition",
              dragActive && "bg-gray-100"
            )}
          >
            <label
              htmlFor="multi-upload-file"
              className="flex flex-col items-center gap-4 cursor-pointer"
            >
              <FontAwesomeIcon
                icon={faFileImport}
                className="h-24 w-24 opacity-50 mr-6"
              />
              <span>{t`Select file(s) to upload or drag and drop them here`}</span>
              <input
                type="file"
                id="multi-upload-file"
                name="multi-upload-file"
                multiple
                className="hidden"
                accept={acceptedFileTypes}
                onChange={handleChange}
                ref={inputRef}
              />
            </label>
          </div>

          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="ml-2 pt-4 pb-1 text-unleashed-gray flex gap-4 items-center max-w-xs"
            >
              <FontAwesomeIcon icon={faFile} />
              <span className="truncate">{file.name}</span>
              <span>{displayFileSize(file.size)}</span>
              {isInvalidFileSize(file.size) && (
                <span className="text-danger text-xs whitespace-nowrap">
                  ({t`Invalid size`})
                </span>
              )}
            </div>
          ))}

          {anyInvalidFiles && (
            <div className="ml-2 pt-4 pb-1 text-unleashed-gray flex gap-4 items-center">
              <span className="text-danger text-xs whitespace-nowrap">
                {t`Some files are larger than the maximum file size`}
              </span>
              <span>{displayFileSize(maxFileSizeBytes)}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              className="inline-flex items-center text-unleashed-pink bg-white hover:bg-primary hover:text-white transition"
              onClick={() => handleShowModal(false)}
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              type="button"
              disabled={noFilesSelected || anyInvalidFiles}
              className={classNames(
                "inline-flex items-center",
                !(noFilesSelected || anyInvalidFiles)
                  ? "hover:bg-white hover:text-primary transition"
                  : "cursor-not-allowed"
              )}
              onClick={handleSubmit}
            >
              <Trans>Upload</Trans>
            </Button>
          </div>
        </div>
      </form>
    </div>
  ) : (
    <></>
  );
};

export default MultipleFileUploadModal;

